// src/components/agencies/AgencyForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createAgency,
  updateAgency,
  fetchAgency,
  clearAgencyError,
  clearAgencySuccess,
} from '../../store/actions/agencyActions';
import { fetchEntities } from '../../store/actions/entityActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const AgencyForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedAgency, loading, error, success } = useSelector(
    (state) => state.agencies
  );
  const { entities } = useSelector((state) => state.entities);

  const [formData, setFormData] = useState({
    name: '',
    entityId: '',
    codeinte: '',
    codetypein: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    lat: '',
    long: '',
    distance: 0,
    rating: 0,
    reviewCount: 0,
    isOpen: true,
  });

  const [errors, setErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!id;

  // Styles object
  const styles = {
    container: {
      maxWidth: '1024px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '2rem',
    },
    section: {
      marginBottom: '2rem',
      paddingBottom: '2rem',
      borderBottom: '1px solid #e5e7eb',
    },
    sectionTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1.5rem',
    },
    fullWidth: {
      gridColumn: 'span 2',
    },
    fieldGroup: {
      marginBottom: '1rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, boxShadow 0.2s',
    },
    inputFocus: {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    select: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      backgroundColor: '#ffffff',
    },
    textarea: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
    },
    inputError: {
      borderColor: '#dc2626',
    },
    errorText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#dc2626',
    },
    helperText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      marginRight: '0.5rem',
      cursor: 'pointer',
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem',
      color: '#374151',
      cursor: 'pointer',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '2rem',
    },
    submitButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    submitButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
  };

  useEffect(() => {
    dispatch(fetchEntities({ limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchAgency(id));
    }
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    if (isEditMode && selectedAgency) {
      setFormData({
        name: selectedAgency.name || '',
        entityId: selectedAgency.entityId || '',
        codeinte: selectedAgency.codeinte || '',
        codetypein: selectedAgency.codetypein || '',
        address: selectedAgency.address || '',
        city: selectedAgency.city || '',
        state: selectedAgency.state || '',
        zipCode: selectedAgency.zipCode || '',
        phone: selectedAgency.phone || '',
        email: selectedAgency.email || '',
        lat: selectedAgency.lat || '',
        long: selectedAgency.long || '',
        distance: selectedAgency.distance || 0,
        rating: selectedAgency.rating || 0,
        reviewCount: selectedAgency.reviewCount || 0,
        isOpen: selectedAgency.isOpen ?? true,
      });
    }
  }, [selectedAgency, isEditMode]);

  // Cleanup
  useEffect(() => {
    return () => {
      dispatch(clearAgencyError());
      dispatch(clearAgencySuccess());
    };
  }, [dispatch]);

  // Memoized validation function
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.entityId) {
      newErrors.entityId = "L'entité est requise";
    }

    if (!formData.codetypein) {
      newErrors.codetypein = 'Le type est requis';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Format d'email invalide";
    }

    if (formData.phone && !/^[0-9+\-\s()]+$/.test(formData.phone)) {
      newErrors.phone = 'Format de téléphone invalide';
    }

    if (formData.lat && (formData.lat < -90 || formData.lat > 90)) {
      newErrors.lat = 'Latitude doit être entre -90 et 90';
    }

    if (formData.long && (formData.long < -180 || formData.long > 180)) {
      newErrors.long = 'Longitude doit être entre -180 et 180';
    }

    if (formData.rating && (formData.rating < 0 || formData.rating > 5)) {
      newErrors.rating = 'La note doit être entre 0 et 5';
    }

    if (formData.reviewCount && formData.reviewCount < 0) {
      newErrors.reviewCount = 'Le nombre d\'avis ne peut pas être négatif';
    }

    return newErrors;
  }, [formData.name, formData.entityId, formData.codetypein, formData.email, 
      formData.phone, formData.lat, formData.long, formData.rating, formData.reviewCount]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  }, []);

  const handleNumberChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === '' ? '' : Number(value),
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        await dispatch(updateAgency(id, formData));
      } else {
        await dispatch(createAgency(formData));
      }
      navigate('/admin/agencies');
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, formData, id, isEditMode, navigate, validateForm]);

  const handleCancel = useCallback(() => {
    if (Object.keys(formData).some(key => formData[key] !== '' && formData[key] !== false && formData[key] !== 0)) {
      setShowCancelConfirm(true);
    } else {
      navigate('/admin/agencies');
    }
  }, [formData, navigate]);

  if (loading && isEditMode) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          {isEditMode ? "Modifier l'agence" : 'Créer une nouvelle agence'}
        </h1>
        <button
          onClick={handleCancel}
          style={styles.cancelButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
          }}
        >
          Annuler
        </button>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearAgencyError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearAgencySuccess())}
        />
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Basic Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Informations de base</h2>
          <div style={styles.grid}>
            <div style={styles.fullWidth}>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="name">
                  Nom de l'agence *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.name ? styles.inputError : {}),
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = styles.inputFocus.borderColor;
                    e.target.style.boxShadow = styles.inputFocus.boxShadow;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = styles.input.borderColor;
                    e.target.style.boxShadow = 'none';
                  }}
                />
                {errors.name && (
                  <div style={styles.errorText}>{errors.name}</div>
                )}
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="entityId">
                Entité *
              </label>
              <select
                id="entityId"
                name="entityId"
                value={formData.entityId}
                onChange={handleChange}
                style={{
                  ...styles.select,
                  ...(errors.entityId ? styles.inputError : {}),
                }}
              >
                <option value="">Sélectionner une entité</option>
                {entities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {entity.name}
                  </option>
                ))}
              </select>
              {errors.entityId && (
                <div style={styles.errorText}>{errors.entityId}</div>
              )}
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="codetypein">
                Type *
              </label>
              <select
                id="codetypein"
                name="codetypein"
                value={formData.codetypein}
                onChange={handleChange}
                style={{
                  ...styles.select,
                  ...(errors.codetypein ? styles.inputError : {}),
                }}
              >
                <option value="">Sélectionner un type</option>
                <option value="AGENCY">Agence principale</option>
                <option value="BRANCH">Succursale</option>
                <option value="POINT">Point de service</option>
                <option value="REPRESENTATIVE">Représentation</option>
              </select>
              {errors.codetypein && (
                <div style={styles.errorText}>{errors.codetypein}</div>
              )}
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="codeinte">
                Code interne
              </label>
              <input
                type="number"
                id="codeinte"
                name="codeinte"
                value={formData.codeinte}
                onChange={handleNumberChange}
                style={styles.input}
              />
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Adresse</h2>
          <div style={styles.grid}>
            <div style={styles.fullWidth}>
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="address">
                  Adresse
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="Numéro et nom de rue"
                />
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="city">
                Ville
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                style={styles.input}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="state">
                Région / Département
              </label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                style={styles.input}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="zipCode">
                Code postal
              </label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                style={styles.input}
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Contact</h2>
          <div style={styles.grid}>
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="phone">
                Téléphone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors.phone ? styles.inputError : {}),
                }}
                placeholder="01 23 45 67 89"
              />
              {errors.phone && (
                <div style={styles.errorText}>{errors.phone}</div>
              )}
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="email">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(errors.email ? styles.inputError : {}),
                }}
                placeholder="contact@agence.fr"
              />
              {errors.email && (
                <div style={styles.errorText}>{errors.email}</div>
              )}
            </div>
          </div>
        </div>

        {/* Location Coordinates */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Coordonnées GPS</h2>
          <div style={styles.grid}>
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="lat">
                Latitude
              </label>
              <input
                type="number"
                id="lat"
                name="lat"
                value={formData.lat}
                onChange={handleNumberChange}
                step="any"
                style={{
                  ...styles.input,
                  ...(errors.lat ? styles.inputError : {}),
                }}
                placeholder="48.8566"
              />
              {errors.lat && (
                <div style={styles.errorText}>{errors.lat}</div>
              )}
              <div style={styles.helperText}>
                Entre -90 et 90
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="long">
                Longitude
              </label>
              <input
                type="number"
                id="long"
                name="long"
                value={formData.long}
                onChange={handleNumberChange}
                step="any"
                style={{
                  ...styles.input,
                  ...(errors.long ? styles.inputError : {}),
                }}
                placeholder="2.3522"
              />
              {errors.long && (
                <div style={styles.errorText}>{errors.long}</div>
              )}
              <div style={styles.helperText}>
                Entre -180 et 180
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="distance">
                Distance (km)
              </label>
              <input
                type="number"
                id="distance"
                name="distance"
                value={formData.distance}
                onChange={handleNumberChange}
                step="0.1"
                min="0"
                style={styles.input}
              />
            </div>
          </div>
        </div>

        {/* Rating Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Évaluations</h2>
          <div style={styles.grid}>
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="rating">
                Note
              </label>
              <input
                type="number"
                id="rating"
                name="rating"
                value={formData.rating}
                onChange={handleNumberChange}
                step="0.1"
                min="0"
                max="5"
                style={{
                  ...styles.input,
                  ...(errors.rating ? styles.inputError : {}),
                }}
              />
              {errors.rating && (
                <div style={styles.errorText}>{errors.rating}</div>
              )}
              <div style={styles.helperText}>
                Entre 0 et 5
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="reviewCount">
                Nombre d'avis
              </label>
              <input
                type="number"
                id="reviewCount"
                name="reviewCount"
                value={formData.reviewCount}
                onChange={handleNumberChange}
                min="0"
                style={{
                  ...styles.input,
                  ...(errors.reviewCount ? styles.inputError : {}),
                }}
              />
              {errors.reviewCount && (
                <div style={styles.errorText}>{errors.reviewCount}</div>
              )}
            </div>
          </div>
        </div>

        {/* Settings */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Paramètres</h2>
          <div style={styles.grid}>
            <div style={styles.fieldGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="isOpen"
                  checked={formData.isOpen}
                  onChange={handleChange}
                  style={styles.checkbox}
                />
                Ouvert actuellement
              </label>
            </div>
          </div>
        </div>

        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={handleCancel}
            style={styles.cancelButton}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              ...styles.submitButton,
              ...(isSubmitting ? styles.submitButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
              }
            }}
          >
            {isSubmitting 
              ? (isEditMode ? 'Modification...' : 'Création...') 
              : (isEditMode ? 'Modifier' : 'Créer')}
          </button>
        </div>
      </form>

      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => navigate('/admin/agencies')}
        title="Annuler les modifications"
        message="Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?"
        confirmText="Quitter"
        cancelText="Continuer l'édition"
        type="warning"
      />
    </div>
  );
};

export default AgencyForm;